import IntegratedEmissionReportStatusControlSection from '../IntegratedEmissionReportStatusControlSection'
import SingleMethodEmissionReportStatusControlSection from '../SingleMethodEmissionReportStatusControlSection'
import { EmissionReport } from '@/api/EmissionReportModel'
import { ProductionRoute } from '@/features/production-routes/api/ProductionRouteModel'
import { ReportCalculatedEmissions } from '@/shared/api/types'
import { RefObject, useRef } from 'react'
import { UnitLabel } from '@/shared/config/config'
import useI18n from '@/shared/hooks/useI18n'

import { usePermissions } from '@/shared/hooks/usePermissions'
import useElementVisibility from '@/hooks/useElementVisibility'
import MiniSingleMethodEmissionReportStatusControlSection from './MiniSingleMethodEmissionReportStatusControlSection'

export default function ReportHeaderSection({
  isIntegratedProcess,
  startDate,
  endDate,
  allMethodsPercentages,
  productionRoute,
  submit,
  isSubmitting,
  calculatedEmissionsForAllMethods,
  openEditActivityLevelDialog,
  percentageComplete,
  report,
  activityLevel,
  isActivityLevelRequired
}: {
  isIntegratedProcess: boolean
  startDate: string
  endDate: string
  allMethodsPercentages: number[]
  productionRoute: ProductionRoute
  submit: () => void
  isSubmitting: boolean
  calculatedEmissionsForAllMethods: ReportCalculatedEmissions
  openEditActivityLevelDialog: () => void
  percentageComplete: number
  report: EmissionReport
  activityLevel: number | null
  isActivityLevelRequired: boolean
}) {
  const statusSectionRef = useRef<HTMLDivElement>(null)
  const { numberFormatter3Decimals } = useI18n()
  const { isVisible: isStatusSectionVisible, isInitialized: isStatusSectionInitialized } =
    useElementVisibility(statusSectionRef as RefObject<HTMLDivElement>, {
      threshold: 0.5
    })

  const isMiniSectionVisible = isStatusSectionInitialized && !isStatusSectionVisible

  const { checkPermission } = usePermissions()
  const canEditReport = checkPermission('emissionReport', 'update')

  const calculatedEmissionsLastMethod =
    calculatedEmissionsForAllMethods?.[calculatedEmissionsForAllMethods.length - 1]

  const formattedTotalDirect = calculatedEmissionsLastMethod?.totalDirectEmissions
    ? `${numberFormatter3Decimals(calculatedEmissionsLastMethod?.totalDirectEmissions)} ${UnitLabel.tCO2e}`
    : '-'
  const formattedTotalIndirect = calculatedEmissionsLastMethod?.totalIndirectEmissions
    ? `${numberFormatter3Decimals(calculatedEmissionsLastMethod?.totalIndirectEmissions)} ${UnitLabel.tCO2e}`
    : '-'

  const formattedSpecificDirect = calculatedEmissionsLastMethod?.specificDirectEmbeddedEmissions
    ? `${numberFormatter3Decimals(calculatedEmissionsLastMethod?.specificDirectEmbeddedEmissions)} ${UnitLabel.tCO2ePerT}`
    : '-'
  const formattedSpecificIndirect = calculatedEmissionsLastMethod?.specificIndirectEmbeddedEmissions
    ? `${numberFormatter3Decimals(calculatedEmissionsLastMethod?.specificIndirectEmbeddedEmissions)} ${UnitLabel.tCO2ePerT}`
    : '-'

  return (
    <>
      <MiniSingleMethodEmissionReportStatusControlSection
        submittedAt={report.submittedAt}
        reportStatus={report.status}
        activityLevel={activityLevel || 0}
        reportProgress={percentageComplete}
        submit={submit}
        isSubmitting={isSubmitting}
        canEditReport={canEditReport}
        formattedTotalDirect={formattedTotalDirect}
        formattedTotalIndirect={formattedTotalIndirect}
        formattedSpecificDirect={formattedSpecificDirect}
        formattedSpecificIndirect={formattedSpecificIndirect}
        isMiniSectionVisible={isMiniSectionVisible}
        isActivityLevelRequired={isActivityLevelRequired}
      />
      {isIntegratedProcess ? (
        <IntegratedEmissionReportStatusControlSection
          startDate={startDate}
          endDate={endDate}
          methodPercentages={allMethodsPercentages}
          reportStatus={report.status}
          productionRouteName={productionRoute.name}
          productionMethods={productionRoute.productionMethods}
          onSubmit={submit}
          isSubmitting={isSubmitting}
          calculatedEmissionsForAllMethods={calculatedEmissionsForAllMethods}
        />
      ) : (
        <div ref={statusSectionRef}>
          <SingleMethodEmissionReportStatusControlSection
            isActivityLevelRequired={isActivityLevelRequired}
            submittedAt={report.submittedAt}
            canEditReport={canEditReport}
            onActivityLevelEdit={openEditActivityLevelDialog}
            reportStatus={report.status}
            reportProgress={percentageComplete}
            submit={submit}
            isSubmitting={isSubmitting}
            activityLevel={activityLevel || 0}
            formattedTotalDirect={formattedTotalDirect}
            formattedTotalIndirect={formattedTotalIndirect}
            formattedSpecificDirect={formattedSpecificDirect}
            formattedSpecificIndirect={formattedSpecificIndirect}
          />
        </div>
      )}
    </>
  )
}
